import React from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin, Check, Clock } from 'lucide-react'
import { Card, CardContent } from '../ui/card'

type RegionStatus = 'available' | 'comingSoon'

interface Region {
    code: string
    status: RegionStatus
    hasArea: boolean
}

// Codes: mvdatacenter/ansible-mining docs/DD_0003_internal-ops-portal.md, "Region codes".
// Statuses: mvdatacenter/engineering-projects DD-0001, "Seed status" (us-1 unavailable shows
// as coming soon). Why the page copies this list: the same DD, "Poland is pl-1".
const regions: Region[] = [
    { code: 'pl-1', status: 'available', hasArea: true },
    { code: 'uae-1', status: 'available', hasArea: true },
    { code: 'us-1', status: 'comingSoon', hasArea: false },
]

const Regions = () => {
    const { t } = useTranslation()

    return (
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
                <h2 className="text-3xl font-bold tracking-tight">{t('regions.title')}</h2>
                <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">{t('regions.subtitle')}</p>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {regions.map((region) => (
                    <Card key={region.code} data-region={region.code} className="transition-shadow hover:shadow-md">
                        <CardContent className="flex items-start gap-4 p-6">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10">
                                <MapPin className="h-6 w-6 text-primary" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <h3 className="text-xl font-semibold">{t(`regions.names.${region.code}`)}</h3>
                                    {region.status === 'available' ? (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                            <Check className="h-3 w-3" />
                                            {t('regions.available')}
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                                            <Clock className="h-3 w-3" />
                                            {t('regions.comingSoon')}
                                        </span>
                                    )}
                                </div>
                                <p className="mt-1 text-sm text-muted-foreground">{region.code}</p>
                                {region.hasArea && (
                                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                        {t(`regions.areas.${region.code}`)}
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </section>
    )
}

export default Regions
